/**
 * Mind Map Utilities
 * localStorage management for React Flow nodes/edges
 */

import { MindMapProfile, MindMapNode, MindMapEdge } from "./mindMapTypes";

const MINDMAP_PROFILES_STORAGE_KEY = "mindmap_profiles_v2";
const MINDMAP_DRAFT_STORAGE_KEY = "mindmap_draft_v2";

/**
 * Generate a unique profile ID
 */
export function generateProfileId(): string {
  return `mindmap_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Generate a unique node ID
 */
export function generateNodeId(): string {
  return `node_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Load all saved mind map profiles from localStorage
 */
export function loadProfiles(): MindMapProfile[] {
  try {
    const stored = localStorage.getItem(MINDMAP_PROFILES_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error("Failed to load mind map profiles:", error);
    return [];
  }
}

/**
 * Save all profiles to localStorage
 */
export function saveProfiles(profiles: MindMapProfile[]): void {
  try {
    localStorage.setItem(
      MINDMAP_PROFILES_STORAGE_KEY,
      JSON.stringify(profiles)
    );
  } catch (error) {
    console.error("Failed to save mind map profiles:", error);
  }
}

export type MindMapDraft = {
  nodes: MindMapNode[];
  edges: MindMapEdge[];
  updatedAt: number;
};

export function loadDraft(): MindMapDraft | null {
  try {
    const stored = localStorage.getItem(MINDMAP_DRAFT_STORAGE_KEY);
    return stored ? (JSON.parse(stored) as MindMapDraft) : null;
  } catch (error) {
    console.error("Failed to load mind map draft:", error);
    return null;
  }
}

export function saveDraft(nodes: MindMapNode[], edges: MindMapEdge[]): void {
  try {
    const payload: MindMapDraft = { nodes, edges, updatedAt: Date.now() };
    localStorage.setItem(MINDMAP_DRAFT_STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.error("Failed to save mind map draft:", error);
  }
}

export function clearDraft(): void {
  try {
    localStorage.removeItem(MINDMAP_DRAFT_STORAGE_KEY);
  } catch (error) {
    console.error("Failed to clear mind map draft:", error);
  }
}

/**
 * Create a new profile
 */
export function createProfile(
  name: string,
  nodes: MindMapNode[],
  edges: MindMapEdge[]
): MindMapProfile {
  const now = Date.now();
  return {
    id: generateProfileId(),
    name,
    nodes,
    edges,
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Update an existing profile
 */
export function updateProfile(
  profiles: MindMapProfile[],
  id: string,
  updates: Partial<Pick<MindMapProfile, "name" | "nodes" | "edges">>
): MindMapProfile[] {
  return profiles.map((profile) =>
    profile.id === id
      ? { ...profile, ...updates, updatedAt: Date.now() }
      : profile
  );
}

/**
 * Delete a profile by ID
 */
export function deleteProfile(
  profiles: MindMapProfile[],
  id: string
): MindMapProfile[] {
  return profiles.filter((profile) => profile.id !== id);
}

/**
 * Find a profile by ID
 */
export function findProfile(
  profiles: MindMapProfile[],
  id: string
): MindMapProfile | undefined {
  return profiles.find((profile) => profile.id === id);
}
